import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import EncyroLink from "../components/EncyroLink";

const SERVICES = [
  "Retirement Planning",
  "Individual Tax Preparation",
  "Small-Business Tax Planning",
  "IRS Notice / Representation",
  "General Consultation",
];

const TIMES = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

function nextBusinessDayISO(daysAhead = 1) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return d.toISOString().slice(0, 10);
}

export default function BookingPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: SERVICES[0],
    preferred_date: nextBusinessDayISO(2),
    preferred_time: "10:00 AM",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/appointments", form);
      toast.success("Booking request received.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <CheckCircle2 size={48} strokeWidth={1.2} className="text-[#8E6A2B] mx-auto mb-8" />
        <h1 className="font-serif text-5xl text-[#0F292F] font-light tracking-tight">Request received.</h1>
        <p className="mt-6 text-lg text-[#52525B] leading-relaxed">
          Thank you. Harold Hodges will personally review your request and confirm by email within one business day.
        </p>
        <div className="mt-12">
          <EncyroLink label="Send Documents Securely (Encyro)" variant="accent" />
        </div>
        <div className="mt-8 text-sm text-[#52525B] max-w-xl mx-auto">
          Already have tax documents you'd like Harold to review before the call?
          Send them through our encrypted Encyro link — no account required.
        </div>
        <div className="mt-12">
          <a href="/" className="btn-secondary">Return Home</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-booking" className="bg-[#F9F8F6]">
      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Initial Consultation</div>
        <h1 className="font-serif text-5xl md:text-6xl text-[#0F292F] font-light tracking-tight leading-[1.05]">
          A 30-minute introductory<br />
          consultation.
        </h1>
        <p className="mt-8 text-lg text-[#52525B] max-w-2xl leading-relaxed">
          Pick a topic — tax planning or retirement planning — choose a date that works,
          and tell us a bit about your situation. We'll confirm by email and let you know
          honestly whether the firm is the right fit.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-24">
        <form onSubmit={submit} data-testid="booking-form" className="bg-white border border-black/10 p-10 md:p-14 space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Full Name *</div>
              <input data-testid="book-name" required className="he-input" value={form.name} onChange={onChange("name")} />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Email *</div>
              <input type="email" data-testid="book-email" required className="he-input" value={form.email} onChange={onChange("email")} />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Phone</div>
              <input data-testid="book-phone" className="he-input" value={form.phone} onChange={onChange("phone")} />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Service *</div>
              <select data-testid="book-service" required className="he-input bg-transparent" value={form.service} onChange={onChange("service")}>
                {SERVICES.map((s) => (<option key={s}>{s}</option>))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Preferred Date *</div>
              <input type="date" data-testid="book-date" required className="he-input" value={form.preferred_date} onChange={onChange("preferred_date")} min={nextBusinessDayISO(1)} />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Preferred Time *</div>
              <select data-testid="book-time" required className="he-input bg-transparent" value={form.preferred_time} onChange={onChange("preferred_time")}>
                {TIMES.map((t) => (<option key={t}>{t}</option>))}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Notes</div>
            <textarea data-testid="book-notes" rows={4} className="he-input resize-none" value={form.notes} onChange={onChange("notes")} placeholder="Anything specific you'd like to cover..." />
          </label>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-[#52525B] max-w-sm">
              No payment is required. This is a complimentary, no-obligation conversation.
            </p>
            <button type="submit" data-testid="book-submit" disabled={submitting} className="btn-primary disabled:opacity-60">
              {submitting ? "Submitting..." : (<>Request Appointment <ArrowRight size={16} /></>)}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
